import { useState, useEffect, ReactNode, useContext, ChangeEvent } from "react";
import styles from "./table.module.css";
import { Button, Drawer, Popover, LoadingShim, Row, Tag, TextInput } from "@narmi/design_system";
import { Case } from "./CaseTable";
import LabeledContentRow from "./LabeledContentRow";
import NotificationContext from "cerulean/src/NotificationContext";
import { Payment } from "./PaymentTable";
import InformationRequestDialog from "./InformationRequestDialog";
import ReturnRequestDialog from "./ReturnRequestDialog";
import ACWPDialog from "./ACWPDialog";
import { dateOptions } from "./PaymentDetailDrawer";
import CaseHistoryList from "./CaseHistoryList";
import CaseStatusAlert from "./CaseStatusAlert";
import RelatedPayments from "./RelatedPayments";
import InformationRequestResponseDialog from "./InformationRequestResponseDialog";
import { useNavigate } from "react-router-dom";
import CopyableCell from "../CopyableCell";
import { CSRF_TOKEN } from "@pages/csrf";
import { useUserConfiguration } from "@hooks/useUserConfiguration";

export type CaseNote = {
  text: string;
  created_at: string;
  author: string;
};

type CaseEventMetadata = {
  resolution_reason: string;
  reject_reason: string | undefined;
  information_request_identification: string | undefined;
  resolution: string;
  amount_cents: number | undefined;
  reason: string | undefined;
  additional_info: string;
};

export type CaseEvent = {
  created_at: string;
  author: string;
  metadata: CaseEventMetadata;
  type: string;
};

export type TaggedNoteEvent = {
  type: "note" | "event";
  content: CaseNote | CaseEvent;
};

export type InformationRequest = {
  kind: "missing" | "incorrect" | "duplicate";
  resolution: "IDUP" | "INFO" | "IPAY" | "NINF" | "PDNG" | null;
  information_request_id: string;
  created_at: string;
  case_id: string;
  corrective_payment_identification: string;
  resolution_related_information: Record<string, unknown> | null;
  additional_payment_information: Record<string, unknown> | null;
  aml: boolean;
  narrative: string | null;
  originator: "N" | "O";
};

export default function CaseDetailDrawer({
  isOpen,
  setIsOpen,
  caseobj,
  refresh,
}: {
  isOpen: boolean;
  setIsOpen: CallableFunction;
  caseobj: Case | null;
  refresh: CallableFunction;
}) {
  const [caseNoteEvents, setCaseNoteEvents]: [Array<TaggedNoteEvent>, CallableFunction] = useState(
    []
  );
  const [payment, setPayment]: [Payment | null, CallableFunction] = useState(null);
  const [returnAction, setReturnAction]: ["accept" | "reject" | null, CallableFunction] =
    useState(null);
  const [acwpAction, setAcwpAction]: ["accept" | "reject" | "block" | null, CallableFunction] =
    useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [morePopoverOpen, setMorePopoverOpen] = useState(false);
  const [informationRequests, setInformationRequests]: [
    Map<string, InformationRequest>,
    CallableFunction,
  ] = useState(new Map());
  const [createInfoReqAction, setCreateInfoReqAction]: [boolean, CallableFunction] =
    useState(false);

  const [infoReqAction, setInfoReqAction]: ["respond" | "close" | null, CallableFunction] =
    useState(null);

  const { sendNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { isFeatureEnabled } = useUserConfiguration();
  const sendOnly = isFeatureEnabled("fednow_send") && !isFeatureEnabled("fednow_receive");

  function morePopoverOptions(cs: Case) {
    let moreButtons: Array<ReactNode> = [];
    if (cs.kind === "return" && cs.status === "open" && payment?.type === "receive" && !sendOnly) {
      moreButtons = [
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setReturnAction("accept");
          }}
          key="accept"
        >
          Accept return request
        </span>,
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setReturnAction("reject");
          }}
          key="reject"
        >
          Reject return request
        </span>,
      ];
    } else if (
      cs.kind === "ACWP" &&
      cs.status === "open" &&
      payment?.type === "receive" &&
      !sendOnly
    ) {
      moreButtons = [
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setAcwpAction("accept");
          }}
          key="accept"
        >
          Accept ACWP
        </span>,
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setAcwpAction("reject");
          }}
          key="reject"
        >
          Reject ACWP
        </span>,
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setAcwpAction("block");
          }}
          key="block"
        >
          Block ACWP
        </span>,
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setCreateInfoReqAction(true);
          }}
          key="info"
        >
          Request information
        </span>,
      ];
    } else if (
      !sendOnly &&
      cs.kind === "information_request" &&
      cs.status === "open" &&
      cs.information_request_ids !== undefined &&
      informationRequests.get(cs.information_request_ids[0])?.originator === "N"
    ) {
      moreButtons = [
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setInfoReqAction("close");
          }}
          key="close"
        >
          Close information request
        </span>,
      ];
    } else if (
      cs.kind === "information_request" &&
      cs.status === "open" &&
      cs.information_request_ids !== undefined &&
      informationRequests.get(cs.information_request_ids[0])?.originator !== "N"
    ) {
      moreButtons = [
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setInfoReqAction("respond");
          }}
          key="respond"
        >
          Respond to information request
        </span>,
      ];
    }

    return moreButtons;
  }

  async function addCaseNote() {
    if (!noteInput || noteInput === "") {
      return;
    }
    setIsLoading(true);
    const resp = await fetch(
      `/v1/fednow/cases/${encodeURIComponent(caseobj!.case_identification)}/notes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": CSRF_TOKEN,
        },
        body: JSON.stringify({
          text: noteInput,
        }),
      }
    );
    if (!resp.ok) {
      sendNotification({
        type: "negative",
        text: "Error creating note. Please contact support.",
      });
      setIsLoading(false);
      return;
    }
    setNoteInput("");
    await fetchCaseEventsNotes();
    setIsLoading(false);
    (document.getElementById("note-input") as HTMLInputElement).value = "";
  }

  async function fetchCaseEventsNotes() {
    if (!caseobj?.case_identification) {
      return;
    }
    const notes = fetch(
      `/v1/fednow/cases/${encodeURIComponent(caseobj.case_identification)}/notes`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const events = fetch(
      `/v1/fednow/cases/${encodeURIComponent(caseobj.case_identification)}/events`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const responses = await Promise.all([notes, events]);

    if (!responses.every((response) => response.ok)) {
      return;
    }
    const notesRes = await responses[0].json();
    const eventsRes = await responses[1].json();
    const combined = notesRes.results
      .map((n: CaseNote) => {
        return { type: "note", content: n };
      })
      .concat(
        eventsRes.results.map((e: CaseEvent) => {
          return { type: "event", content: e };
        })
      );
    combined.sort((a: TaggedNoteEvent, b: TaggedNoteEvent) => {
      return new Date(b.content.created_at).getTime() - new Date(a.content.created_at).getTime();
    });
    setCaseNoteEvents(combined);
  }

  async function fetchPayment() {
    if (!caseobj?.payment_identification) {
      return;
    }
    const lookupRes = await fetch(
      `/v1/fednow/payments/${encodeURIComponent(caseobj.payment_identification)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!lookupRes.ok) {
      return;
    }
    const result = await lookupRes.json();
    setPayment(result);
  }

  async function fetchInformationRequests() {
    if (!caseobj?.case_identification) {
      return;
    }
    const lookupRes = await fetch(
      `/v1/fednow/cases/${encodeURIComponent(caseobj.case_identification)}/information_requests`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!lookupRes.ok) {
      return;
    }
    const result = await lookupRes.json();

    const informationRequests = new Map<string, InformationRequest>();
    result.map((i: InformationRequest) => {
      informationRequests.set(i.information_request_id, i);
    });
    setInformationRequests(informationRequests);
  }

  useEffect(() => {
    if (caseobj) {
      fetchCaseEventsNotes();
      fetchPayment();
      fetchInformationRequests();
    }
  }, [caseobj, isOpen]);

  if (!caseobj || payment === null) {
    return null;
  }

  const menuOptions = morePopoverOptions(caseobj);

  return (
    <Drawer
      depth="60%"
      isOpen={isOpen}
      showControls={false}
      onUserDismiss={() => {
        navigate("/fednow/cases");
        setIsOpen(false);
      }}
    >
      <LoadingShim isLoading={isLoading}>
        <Row alignItems="center">
          <Row.Item>
            <h2 className="whiteSpace--truncate" style={{ maxWidth: "100%" }}>
              {caseobj.case_identification}
            </h2>
          </Row.Item>
          <Row.Item shrink>
            <div style={{ textTransform: "capitalize" }}>
              <Tag label={caseobj.status}></Tag>
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div style={{ textTransform: "capitalize" }}>
              <Tag label={caseobj.kind}></Tag>
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div
              style={{
                float: "right",
                borderRight: menuOptions.length > 0 ? "2px solid lightGrey" : "none",
              }}
              className={menuOptions.length > 0 ? "padding--right--s" : ""}
            >
              <CopyableCell
                copyText={`${window.location.origin}/fednow/cases/${caseobj.case_identification}`}
                text="Copy link"
                style={{ color: "var(--theme-primary)" }}
                buttonSize="s"
              />
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div style={{ float: "right" }}>
              {menuOptions.length > 0 && (
                <Popover
                  isOpen={morePopoverOpen}
                  content={<div style={{ display: "table" }}>{menuOptions}</div>}
                >
                  <Button
                    kind="plain"
                    startIcon="more-vertical"
                    onClick={() => setMorePopoverOpen(!morePopoverOpen)}
                  />
                </Popover>
              )}
            </div>
          </Row.Item>
        </Row>
        <CaseStatusAlert
          caseobj={caseobj}
          history={caseNoteEvents}
          informationRequests={informationRequests}
        />
        <div className={`${styles.transactionDetailContainer} margin--y padding--all`}>
          <h4 className="fontFamily--body padding--bottom">Case details</h4>
          <LabeledContentRow label="Case ID" content={caseobj.case_identification} />
          <LabeledContentRow
            label="Last updated"
            content={new Date(caseobj.updated_at).toLocaleString("en-US", dateOptions)}
          />
          {caseobj.deadline !== null && (
            <LabeledContentRow
              label="Due"
              content={new Date(caseobj.deadline!).toLocaleString("en-US", dateOptions)}
            />
          )}
        </div>
        <div className={`${styles.transactionDetailContainer} padding--x margin--bottom`}>
          <Row alignItems="center">
            <Row.Item>
              <div className="padding--y">
                <h4 className="fontFamily--body">Case history</h4>
              </div>
            </Row.Item>
          </Row>
          <CaseHistoryList
            history={caseNoteEvents}
            caseobj={caseobj}
            payment={payment}
            informationRequests={informationRequests}
          />
        </div>
        <div className={`${styles.transactionDetailContainer} padding--x`}>
          <Row alignItems="center">
            <Row.Item>
              <div className="">
                <h4 className="fontFamily--body padding--y">Add note</h4>
              </div>
            </Row.Item>
            <Row.Item shrink>
              <div>
                <Button label="Save" kind="plain" onClick={() => addCaseNote()} />
              </div>
            </Row.Item>
          </Row>
          <Row>
            <Row.Item>
              <div className="margin--bottom">
                <TextInput
                  label="Case note"
                  value={noteInput}
                  id="note-input"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setNoteInput(e.target.value);
                  }}
                />
              </div>
            </Row.Item>
          </Row>
        </div>
        <RelatedPayments original={payment} includeOriginal={true} />
        {caseobj.kind === "return" && (
          <ReturnRequestDialog
            caseobj={caseobj}
            payment={payment}
            action={returnAction}
            setAction={setReturnAction}
            successCallback={() => {
              refresh();
            }}
          />
        )}
        {caseobj.kind === "ACWP" && (
          <>
            <ACWPDialog
              caseobj={caseobj}
              payment={payment}
              action={acwpAction}
              setAction={setAcwpAction}
              successCallback={() => {
                refresh();
              }}
            />
            <InformationRequestDialog
              caseobj={caseobj}
              payment={payment}
              shown={createInfoReqAction}
              setShown={setCreateInfoReqAction}
              successCallback={() => {
                refresh();
              }}
            />
          </>
        )}
        {caseobj.kind === "information_request" && (
          <InformationRequestResponseDialog
            action={infoReqAction}
            setAction={setInfoReqAction}
            caseobj={caseobj}
            informationRequests={informationRequests}
            successCallback={() => refresh()}
          />
        )}
      </LoadingShim>
    </Drawer>
  );
}
